import {applyMiddleware, compose, createStore, Store} from "redux";
import {composeWithDevTools} from "redux-devtools-extension/developmentOnly";
import createSagaMiddleware from "redux-saga";
import rootReducer, {IApplicationState, rootSaga} from "./index";
import {gameInitialState} from "./game/reducer";
import {settingsInitialState} from "./settings/reducer";
import {playerInitialState} from "./player/reducer";
import {questionsInitialState} from "./questions/reducer";
import {hintsInitialState} from "./hints/reducer";
import {tutorialInitialState} from "./tutorial/reducer";
import {touch, music, sounds} from "../shared/sounds";
import merge from "../shared/utils/merge";
import stateFlowEnhancer from "./common/stateFlowEnhancer";
import {analyticsMiddleware} from "./common/analytics/analyticsMiddleware";
import { gameEventsMiddleware } from "./common/gameEventsMiddleware";

function getInitialState(serverState: Partial<IApplicationState>) {
    serverState = merge(
        window.INITIAL_STATE,
        serverState,
        window.OVERRIDE_STATE
    );

    let state = merge({
            settings: settingsInitialState,
            game: gameInitialState,
            player: {
                ...playerInitialState,
                coins: serverState?.settings?.gameBalance?.startingCoins || settingsInitialState.gameBalance.startingCoins
            },
            questions: questionsInitialState,
            hints: hintsInitialState,
            tutorial: tutorialInitialState,
        },
        serverState
    );

    if (state.player.lives < state.settings.gameBalance.maxLives && state.player.lastLiveIncrementTimestamp) {
        const liveIncrement = Math.floor((new Date().getTime() - state.player.lastLiveIncrementTimestamp) / state.settings.gameBalance.restoreLivesInterval);

        state = merge(state, {
            player: {
                lives: Math.min(state.settings.gameBalance.maxLives, state.player.lives + liveIncrement),
                lastLiveIncrementTimestamp: new Date().getTime()
            }
        } as Partial<IApplicationState>);
    }

    return state;
}

function configureSounds(state: Partial<IApplicationState>) {
    const enableSounds = () => {
        touch();

        if (state && state.player && state.player.settings && state.player.settings.music) {
            music.mute(false);
        }

        if (state && state.player && state.player.settings && state.player.settings.sounds) {
            sounds.mute(false);
        }

        document.removeEventListener("click", enableSounds, false);
    };

    document.addEventListener("click", enableSounds, false);
}

export default function configureStore(serverState: Partial<IApplicationState>): Store<IApplicationState> {
    const sagaMiddleware = createSagaMiddleware({
        onError: error => {
            window.onerror && window.onerror("Uncaught error in redux saga", "configureStore.ts", -1, -1, error);
        }
    });
    const initialState = getInitialState(serverState);

    const store = createStore(
        rootReducer,
        initialState,
        composeWithDevTools(compose(
            applyMiddleware(sagaMiddleware),
            applyMiddleware(analyticsMiddleware),
            applyMiddleware(gameEventsMiddleware),
            stateFlowEnhancer(),
        ))
    );

    configureSounds(initialState);

    sagaMiddleware.run(rootSaga);

    return store;
}